// dependencies
import React, { useMemo } from "react";

// components
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons";

// py-components
import {
	Checkbox,
	ICellRendererParams,
	Icon,
	Link,
	PYColumnDef,
	SortChangedEvent,
	Table,
	TableRef,
	Text,
	Tooltip,
} from "@performyard/py-components";

// hooks
import { SearchServiceSort, useCompanySettings } from "../../../../../../api/hooks";

import { Goal } from "../../../../../../api/services/searchServiceResultTypes/goal";

import get from "../../../../../../utils/GetNested";

interface BaseProps {
	goals: Goal[];
	isFetchingGoals: boolean;
	handleColumnHeaderClick: (columnId: SearchServiceSort) => void;
	activeSort: string;
	activeSortDirection: string;
	isBulkSelectionEnabled?: boolean;
	selectedGoalIds?: string[];
	onAddGoals?: (goalIds: string[]) => void;
	onRemoveGoals?: (goalIds: string[]) => void;
	allPageRowsSelected?: boolean;
	onSelectAllPageChange?: (isSelected: boolean) => void;
	isGroupFieldVisible?: boolean;
}

// Custom cell renderer for goal name with link and optional checkbox
const GoalNameCellRenderer = (params: ICellRendererParams<Goal>) => {
	const goal = params.data;
	if (!goal) return null;

	const goalHref = `/goal/${get(goal, "_id", "")}/`;
	const userCanViewGoal = goal.permissions?.includes("goals:viewDetails") || false;
	const goalName = get(goal, "name", "");

	// Get bulk selection state from context
	const { selectedGoalIds, onAddGoals, onRemoveGoals, isBulkSelectionEnabled } = params.context || {};
	const isSelected = selectedGoalIds?.includes(goal._id);

	return (
		<div data-testid="name" className="flex h-full w-full min-w-0 max-w-full items-center justify-start">
			{/* Checkbox appears on the left when bulk selection is enabled */}
			<div
				className={`duration-2000 flex items-center overflow-hidden transition-all ease-in-out ${
					isBulkSelectionEnabled ? "mr-2 w-5 opacity-100" : "w-0 opacity-0"
				}`}
			>
				<Checkbox
					size="small"
					id={`${goal._id}-selection`}
					checked={isSelected}
					onCheckedChange={(value) => {
						if (value) {
							onAddGoals?.([goal._id]);
						} else {
							onRemoveGoals?.([goal._id]);
						}
					}}
				/>
			</div>
			{/* Name content */}
			<div className="min-w-0 flex-1">
				{userCanViewGoal ? (
					<Link href={goalHref}>
						<Text size="paragraph-3" color="primary-5" dangerouslySetClassName="line-clamp-1">
							{goalName}
						</Text>
					</Link>
				) : (
					<div className="w-full min-w-0 py-line-clamp-1">
						<Text size="paragraph-3">{goalName}</Text>
					</div>
				)}
			</div>
		</div>
	);
};

// Custom cell renderer for visibility indicator
const VisibilityCellRenderer = (params: ICellRendererParams<Goal>) => {
	const goal = params.data;
	if (!goal) return null;

	const isPrivateGoal = get(goal, "transparency", "") === "private";

	return (
		<div data-testid="visibilityContainer" className="flex h-full items-center justify-center">
			<Tooltip content={isPrivateGoal ? "Private Goal" : "Public Goal"}>
				<Icon icon={faEyeSlash} color={isPrivateGoal ? "primary-5" : "neutral-3"} />
			</Tooltip>
		</div>
	);
};

// Base column definitions
const createColumnDefs = (
	goalWeightEnabled: boolean,
	groupsEnabled: boolean,
	isGroupFieldVisible: boolean
): PYColumnDef<Goal>[] => {
	let nameColumnWidth = 502;
	if (goalWeightEnabled) {
		nameColumnWidth -= 72;
	}
	if (groupsEnabled && isGroupFieldVisible) {
		nameColumnWidth -= 96;
	}

	const columns: PYColumnDef<Goal>[] = [];

	// Name column with integrated checkbox
	columns.push(
		{
			headerName: "Name",
			field: "name",
			cellRenderer: GoalNameCellRenderer,
			width: nameColumnWidth,
			sortable: true,
			resizable: false,
		},
		{
			headerName: "",
			field: "transparency",
			cellRenderer: VisibilityCellRenderer,
			width: 24,
			sortable: false,
			resizable: false,
		}
	);

	// Add group column if enabled and visible to current user
	if (groupsEnabled && isGroupFieldVisible) {
		columns.push({
			headerName: "Group",
			field: "assignee.group.name",
			type: "text",
			width: 116,
			sortable: true,
			resizable: false,
			valueGetter: (params) => params.data?.assignee?.group?.name ?? "",
		});
	}

	columns.push({
		headerName: "Assignee",
		field: "assignee.name",
		type: "employee",
		width: 180,
		sortable: true,
		resizable: false,
		valueGetter: (params) => params.data?.assignee,
	});

	// Add weight column if enabled
	if (goalWeightEnabled) {
		columns.push({
			headerName: "Weight",
			field: "goal_weight",
			type: "text",
			width: 72,
			sortable: true,
			resizable: false,
			valueFormatter: (params) => {
				const weight = params.value;
				return weight != null ? String(weight) : "";
			},
		});
	}

	columns.push(
		{
			headerName: "Due Date",
			field: "end_date",
			type: "date",
			width: 104,
			sortable: true,
			resizable: false,
			cellRendererParams: {
				dateFormat: "MMM d, yyyy",
			},
			valueGetter: (params) => params.data?.endDate,
		},
		{
			headerName: "Status",
			field: "status",
			type: "text",
			width: 72,
			sortable: true,
			resizable: false,
			valueFormatter: (params) => {
				const status = params.value === "done" ? "completed" : params.value;
				return status ? status.charAt(0).toUpperCase() + status.slice(1) : "";
			},
		},
		{
			headerName: "Progress",
			field: "progress",
			type: "progress",
			width: 180,
			sortable: true,
			resizable: false,
			valueGetter: (params) => {
				const goal = params.data;
				if (!goal) return null;
				return get(goal, "progress", 0) * 100;
			},
			cellRendererParams: (goal: Goal) => ({
				colorOverride: get(goal, ["outlook", "color"], undefined),
			}),
		}
	);

	return columns;
};

const GoalsListTable = (props: BaseProps) => {
	const {
		goals,
		isFetchingGoals,
		handleColumnHeaderClick,
		activeSort,
		activeSortDirection,
		isBulkSelectionEnabled = false,
		selectedGoalIds = [],
		onAddGoals,
		onRemoveGoals,
		allPageRowsSelected = false,
		onSelectAllPageChange,
		isGroupFieldVisible = true,
	} = props;
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const goalWeightEnabled = Boolean(companySettings?.functions?.goals?.attributes["goalWeights"]);
	const groupsEnabled = Boolean(companySettings?.functions?.groups?.state === "enabled");

	const ref = React.useRef<TableRef<Goal>>(null);

	// Memoize column definitions to prevent recreation on every render
	const columnDefs = useMemo(
		() => createColumnDefs(goalWeightEnabled, groupsEnabled, isGroupFieldVisible),
		[goalWeightEnabled, groupsEnabled, isGroupFieldVisible, isBulkSelectionEnabled]
	);

	// Create context object that updates when selection state changes
	const gridContext = useMemo(
		() => ({
			selectedGoalIds,
			onAddGoals,
			onRemoveGoals,
			allPageRowsSelected,
			onSelectAllPageChange,
			isFetchingGoals,
			isBulkSelectionEnabled,
		}),
		[
			selectedGoalIds,
			onAddGoals,
			onRemoveGoals,
			allPageRowsSelected,
			onSelectAllPageChange,
			isFetchingGoals,
			isBulkSelectionEnabled,
		]
	);

	// Refresh checkbox cells and header when selection state changes
	React.useEffect(() => {
		if (ref.current) {
			ref.current.api?.refreshCells({
				columns: ["name"], // Refresh name column since it contains the checkboxes
				force: true,
			});
			ref.current.api?.refreshHeader();
		}
	}, [selectedGoalIds, isBulkSelectionEnabled, allPageRowsSelected]);

	// Handle sort changes from AG Grid
	const onSortChanged = (event: SortChangedEvent<Goal>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn && sortedColumn.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="goalListContainer">
			<Table<Goal>
				ref={ref}
				autoHeight
				rowData={goals}
				columnDefs={columnDefs}
				loading={isFetchingGoals || isCompanySettingsLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText={"No Goals Found"}
				context={gridContext}
				// Set initial sort state from props
				initialState={{
					sort: {
						sortModel: activeSort
							? [
									{
										colId: activeSort,
										sort: activeSortDirection as "asc" | "desc",
									},
								]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default GoalsListTable;
